import { SagaIterator } from 'redux-saga';
import { call, put, takeEvery } from 'redux-saga/effects';

import { Set } from '~commonInfra/utils/Set';
import { addToIdentityPoolOnSiteUser } from '~src/authentication/components/identityPools/actions/AddToIdentityPoolAction';
import { removeFromIdentityPoolOnSiteUser } from '~src/authentication/components/identityPools/actions/RemoveFromIdentityPoolAction';
import { showResetUsersMFADialog } from '~src/settings/siteAuthSettings/components/ResetUserMFAConfirmationDialog';
import { reloadSpace } from '~src/spacesPlatform/sagas/content/RefreshContentSagas';

import { forcePasswordUpdateOnSiteUsers } from '../../authentication/actions/ForcePasswordUpdateAction';
import { resetLoginRateLimitingOnSiteUsers } from '../../authentication/actions/ResetLoginRateLimitingAction';
import {
  SiteUserAddToIdentityPool,
  SiteUserEditGroupMembership, SiteUserEditSiteRole,
  SiteUserForcePasswordUpdate, SiteUserRemove, SiteUserRemoveFromIdentityPool,
  SiteUserResetLoginRateLimiting,
  SiteUserResetMFA,
  SiteUserSetAuthentication
} from '../../bootstrap/redux/actions/AppActionTypes';
import { requestFailed } from '../../bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '../../codegen/Localize';
import { UpdateUserAuthenticationAction } from '../../settings/siteAuthSettings/services/UpdateUserAuthenticationAction';
import { showEditGroupMembershipDialog } from '../groups/services/actions/EditGroupMembershipDialogAction';
import { RemoveUsersAction } from '../services/RemoveUsersAction';
import { SetIntegrationUserSiteRoleAction } from '../services/SetIntegrationUserSiteRoleAction';
import { SetSiteUsersSiteRoleAction } from '../services/SetSiteUsersSiteRoleAction';
import { SiteUserMenuAction } from '../users/actions/SiteUserMenuActions';
import { ISiteUserInfo } from '../users/models/ISiteUserInfo';
import { UserCategory } from '../users/serverApi/types/UserCategory';

export function removeUsers(users: ISiteUserInfo[]) {
  return RemoveUsersAction.showRemoveUsersFromSiteDialog(Set.fromIdItemArray(users));
}

export function* handleRemoveUsers(action: SiteUserMenuAction): SagaIterator {
  try {
    yield call(removeUsers, action.items);

    // Need to reload space for license bar updates
    yield call(reloadSpace);
  } catch (e) {
    // This is to prevent error notifcation from showing up when the dialog is dismissed
    // To be fixed after TFSID 1121927
    return;
  }
}

export function editSiteRole(users: ISiteUserInfo[]) {
  if (users[0]?.userCategory === UserCategory.Integration) {
    return SetIntegrationUserSiteRoleAction.showSetIntegrationUserSiteRoleDialog(Set.fromIdItemArray(users));
  }
  return SetSiteUsersSiteRoleAction.showSetSiteUsersSiteRoleDialog(Set.fromIdItemArray(users));
}

export function* handleEditSiteRole(action: SiteUserMenuAction): SagaIterator {
  try {
    yield call(editSiteRole, action.items);

    // Need to reload space for license bar updates
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function editGroupMembership(users: ISiteUserInfo[]) {
  return showEditGroupMembershipDialog(Set.fromIdItemArray(users));
}

export function* handleEditGroupMembership(action: SiteUserMenuAction): SagaIterator {
  try {
    yield call(editGroupMembership, action.items);

    // Need to reload space for license bar updates
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function setAuthentication(users: ISiteUserInfo[]) {
  return UpdateUserAuthenticationAction.showUpdateUserAuthenticationDialog(Set.fromIdItemArray(users));
}

export function* handleSetAuthentication(action: SiteUserMenuAction): SagaIterator {
  try {
    yield call(setAuthentication, action.items);

    // Need to reload space for license bar updates
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function resetMFA(users: ISiteUserInfo[]) {
  return showResetUsersMFADialog(users);
}

export function* handleResetMFA(action: SiteUserMenuAction): SagaIterator {
  try {
    yield call(resetMFA, action.items);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function forcePasswordUpdate(users: ISiteUserInfo[]) {
  return forcePasswordUpdateOnSiteUsers(Set.fromIdItemArray(users));
}

export function* handleForcePasswordUpdate(action: SiteUserMenuAction): SagaIterator {
  try {
    yield call(forcePasswordUpdate, action.items);

    // Need to reload space for license bar updates
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function resetLoginRateLimiting(users: ISiteUserInfo[]) {
  return resetLoginRateLimitingOnSiteUsers(Set.fromIdItemArray(users));
}

export function* handleResetLoginRateLimiting(action: SiteUserMenuAction): SagaIterator {
  try {
    yield call(resetLoginRateLimiting, action.items);

    // Need to reload space for license bar updates
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function addToIdentityPool(users: ISiteUserInfo[]) {
  return addToIdentityPoolOnSiteUser(users[0]);
}

export function* handleAddToIdentityPool(action: SiteUserMenuAction): SagaIterator {
  try {
    yield call(addToIdentityPool, action.items);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  } finally {
    /* When a new identity needs to be created as part of the add user to pool action, somehow we get a cancel error
       even though identity creation and add to pool are all done successfully.
       Reload the subspace anyway to update user's pool info.
    */
    yield call(reloadSpace);
  }
}

export function removeFromIdentityPool(users: ISiteUserInfo[]) {
  return removeFromIdentityPoolOnSiteUser(users[0]);
}

export function* handleRemoveFromIdentityPool(action: SiteUserMenuAction): SagaIterator {
  try {
    yield call(removeFromIdentityPool, action.items);

    // Need to reload space for license bar updates
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootSiteUserMenuActionSaga(): SagaIterator {
  yield takeEvery(SiteUserEditGroupMembership, handleEditGroupMembership);
  yield takeEvery(SiteUserEditSiteRole, handleEditSiteRole);
  yield takeEvery(SiteUserRemove, handleRemoveUsers);
  yield takeEvery(SiteUserSetAuthentication, handleSetAuthentication);
  yield takeEvery(SiteUserResetMFA, handleResetMFA);
  yield takeEvery(SiteUserForcePasswordUpdate, handleForcePasswordUpdate);
  yield takeEvery(SiteUserResetLoginRateLimiting, handleResetLoginRateLimiting);
  yield takeEvery(SiteUserAddToIdentityPool, handleAddToIdentityPool);
  yield takeEvery(SiteUserRemoveFromIdentityPool, handleRemoveFromIdentityPool);
}
